import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiDigitalWalletTokenListResponse, ApiDigitalWalletTokenResponse } from '../openapi';
import { useApiClient } from '../contexts/ApiContext';
import { useHasRole } from './access';
import { useIsCardsAvailable } from './customers';
import { mapResponse } from './helpers';
import { useEffect, useMemo } from 'react';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';

export function useDigitalWalletTokenListQuery() {
  const client = useApiClient();
  const isAdmin = useHasRole('ADMIN');
  const isCardsAvailable = useIsCardsAvailable();

  const query = useQuery({
    queryKey: ['digital-wallet-tokens'],
    queryFn: () =>
      client
        .GET('/digitalWalletToken' as unknown as never)
        .then(mapResponse)
        .then((it) => ApiDigitalWalletTokenListResponse.parse(it))
        .then((it) => it.digitalWalletTokens),
    retry: true,
    refetchInterval: 30_000,
    enabled: isAdmin && isCardsAvailable,
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    if (query.data) {
      for (const it of query.data) {
        queryClient.setQueryData(['digital-wallet-tokens', it.id], it);
      }
    }
  }, [query.data, queryClient]);

  return query;
}

export function useActiveDigitalWalletTokenList() {
  const listQuery = useDigitalWalletTokenListQuery();
  const filtered = useMemo(
    () =>
      (listQuery.data ?? []).filter((it) => it.state !== 'TERMINATED' && it.state !== 'UNKNOWN'),
    [listQuery.data],
  );

  return filtered;
}

export function useDigitalWalletTokenQuery({ tokenId }: { tokenId: string }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['digital-wallet-tokens', tokenId],

    queryFn: () =>
      client
        .GET(`/digitalWalletToken/${tokenId}` as unknown as never)
        .then(mapResponse)
        .then((it) => ApiDigitalWalletTokenResponse.parse(it))
        .then((it) => it.digitalWalletToken),
    refetchInterval: (query) => (query.state.data?.state === 'PROVISIONING' ? 5_000 : 30_000),
  });
}

export function useDigitalWalletTokenResolveMutation({ tokenId }: { tokenId: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['digital-wallet-tokens', tokenId, 'resolve'],
    mutationFn: ({ action }: { action: 'provision' | 'terminate' }) =>
      client
        .POST(
          `/digitalWalletToken/${tokenId}/${action}` as unknown as never,
          {
            headers: {
              'Idempotency-Key': createIdempotencyKey(),
            },
          } as unknown as never,
        )
        .then(mapResponse),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['digital-wallet-tokens', tokenId] });
      queryClient.invalidateQueries({ queryKey: ['digital-wallet-tokens'] });
    },
  });
}
